<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .wallet-dashboard-page
    web-page-header(
      title="Giving Wallet"
      :subtitle="walletSubtitle"
    )
    .page-container
      // Skeleton loader for wallet card
      .wallet-info-card.skeleton-wallet(v-if="isLoading")
        .skeleton-line.skeleton-title
        .skeleton-line.skeleton-balance

      // Actual wallet card
      .wallet-info-card(v-else-if="!isLoading && hasWallet")
        .wallet-content
          .wallet-header
            .wallet-title Balance
            .wallet-balance ${{ walletBalance.toFixed(2) }}
          .wallet-details(v-if="showDetailedAmounts")
            | Real: ${{ realAmountInWallet.toFixed(2) }} •
            | Reserved: ${{ reservedAmountInWallet.toFixed(2) }}
          .wallet-action
            button.text-btn-add-money(@click="handleAddFunds") Add Money

      nav.wallet-links(v-if="!isLoading && hasWallet" aria-label="Wallet")
        router-link.wallet-link(v-for="link in walletLinks" :key="link.to" :to="link.to")
          | {{ link.label }}
          img.wallet-link__chevron(src="@/assets/icons/chevron-right.svg" alt="")

      // No wallet state (only shown after loading completes)
      .wallet-info-card(v-else-if="!isLoading && !hasWallet")
        .no-wallet
          p You don't have a wallet yet.
          el-button(
            type="primary"
            @click="goToCreateWallet"
          ) Create Giving Wallet

      // Show activity feed immediately (don't wait for wallet to load)
      .activity-feed(v-if="isLoading || hasWallet")
        .section-header
          h3 Activity Feed

        .transactions-container
          // Skeleton loader for initial transactions load
          .transactions-list(v-if="isLoadingTransactions && transactions.length === 0")
            .skeleton-transaction(v-for="n in 5" :key="n")

          .empty-state(v-if="transactions.length === 0 && !isLoadingTransactions")
            p No activity found

          .transactions-list(v-if="transactions.length > 0")
            .transaction-wrapper(v-for="(transaction, index) in transactions" :key="transaction.id")
              TransactionItem(:transaction="transaction")
              .divider(v-if="index < transactions.length - 1")

          .loading-more(v-if="isLoadingTransactions && transactions.length > 0")
            p Loading more...

          el-button.btn-load-more(
            v-if="hasNextPage && !isLoadingTransactions && transactions.length > 0"
            @click="loadMore"
          ) Load More

          .error-state(v-if="transactionsError")
            p {{ transactionsError }}
            el-button(@click="retryTransactions") Retry
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted,
} from 'vue';
import { onBeforeRouteLeave, useRouter, useRoute } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { makeRequest } from '@/graphs/index';
import {
  benevityDetails,
  benevityDetailsForOrg,
  benevityTransactions,
  benevityTransactionsForOrg,
  createWebIntent,
  createWebIntentForOrg,
} from '@/graphs/gql';
import globalShared from '@/shared';
import { useActingEntity } from '@/composables/useActingEntity';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import WebPageHeader from '../components/WebPageHeader.vue';
import TransactionItem from './TransactionItem.vue';
import { WALLET_LINKS } from './walletLinks';

export default defineComponent({
  name: 'WalletDashboard',
  components: {
    WebAppLayout,
    WebPageHeader,
    TransactionItem,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { getAuthToken } = globalShared();
    // The wallet of whoever the user is acting as (themselves or an organization)
    const { organization: actingOrganization, orgId: actingOrgId } = useActingEntity();
    const walletSubtitle = computed(() => (actingOrganization.value
      ? `${actingOrganization.value.name}'s wallet: add money and give to the causes you care about`
      : 'Add money and give to the causes you care about'));

    console.log('[USER WALLET] Wallet dashboard initialized for user');

    const isLoading = ref(false);
    const hasWallet = ref(false);
    const walletBalance = ref(0);
    const realAmountInWallet = ref(0);
    const reservedAmountInWallet = ref(0);
    const transactions = ref<any[]>([]);
    const currentPage = ref(0);
    const hasNextPage = ref(true);
    const isLoadingTransactions = ref(false);
    const transactionsError = ref<string | null>(null);

    const showDetailedAmounts = computed(() => Math.abs(reservedAmountInWallet.value) > 0.01);

    const loadWalletDetails = async () => {
      isLoading.value = true;
      try {
        const token = getAuthToken();
        console.log('[USER WALLET] Fetching user wallet details...');
        
        const orgId = actingOrgId.value;
        const response = await makeRequest(
          'query',
          orgId ? benevityDetailsForOrg : benevityDetails,
          orgId ? { orgId } : {},
          { token },
        );

        if (response?.data) {
          const data = orgId ? response.data.benevityDetailsForOrg : response.data.benevityDetails;

          hasWallet.value = data?.hasWallet || false;
          walletBalance.value = parseFloat(data?.amountInWallet || 0);

          // Handle optional fields
          realAmountInWallet.value = parseFloat(
            data?.realAmountInWallet || data?.amountInWallet || 0,
          );
          reservedAmountInWallet.value = parseFloat(data?.reservedAmountInWallet || 0);

          console.log('[USER WALLET] Has wallet:', hasWallet.value, '| Balance:', walletBalance.value);

          // If user doesn't have a wallet, redirect to create wallet page
          if (!hasWallet.value) {
            router.push('/web/wallet/create');
          }
        }
      } catch (error) {
        console.error('[USER WALLET ERROR]', error);
        ElMessage.error('Failed to load wallet details');
      } finally {
        isLoading.value = false;
      }
    };

    const fetchTransactionsPage = async (page: number) => {
      isLoadingTransactions.value = true;
      transactionsError.value = null;

      try {
        const skip = page * 15;
        const take = 15;
        const token = getAuthToken();

        const orgId = actingOrgId.value;
        const response = await makeRequest(
          'query',
          orgId ? benevityTransactionsForOrg : benevityTransactions,
          orgId ? { orgId, skip, take } : { skip, take },
          { token },
        );

        if (response?.data) {
          const data = orgId
            ? response.data.benevityTransactionsForOrg
            : response.data.benevityTransactions;

          // Append or replace transactions
          if (page === 0) {
            transactions.value = data || [];
          } else {
            transactions.value.push(...(data || []));
          }

          currentPage.value = page;
          hasNextPage.value = (data?.length || 0) >= take;
        }
      } catch (error) {
        console.error('Error loading transactions:', error);
        transactionsError.value = 'Failed to load transactions';
      } finally {
        isLoadingTransactions.value = false;
      }
    };

    const loadMore = () => {
      fetchTransactionsPage(currentPage.value + 1);
    };

    const retryTransactions = () => {
      fetchTransactionsPage(0);
    };

    const goToCreateWallet = () => {
      router.push('/web/wallet/create');
    };

    const handleAddFunds = async () => {
      try {
        const token = getAuthToken();
        const orgId = actingOrgId.value;
        const response = await makeRequest(
          'mutation',
          orgId ? createWebIntentForOrg : createWebIntent,
          orgId ? { orgId } : {},
          { token },
        );

        if (response?.data) {
          let paymentUrl = orgId
            ? response.data.createWebIntentForOrg
            : response.data.createWebIntent;

          // Check if this is an internal payment URL (Stripe) or external (Benevity)
          // Internal URLs contain '/payment' path, external ones are to different domains
          const isInternalPaymentUrl = paymentUrl.includes('/payment');

          // If it's an internal payment URL (Stripe), add isWebUser parameter
          if (isInternalPaymentUrl) {
            // Parse URL to properly add query parameter
            const url = new URL(paymentUrl, window.location.origin);
            url.searchParams.set('isWebUser', 'true');

            // Stay on this deployment: the backend builds the link with its own web base URL,
            // which on PR/QA builds and localhost is a different site (SPO-1210)
            paymentUrl = url.pathname + url.search;
          }

          // Redirect to payment URL
          window.location.href = paymentUrl;
        } else if (response?.errors) {
          console.error('Error initiating payment:', response.errors);
          ElMessage.error(response.errors[0]?.message || 'Failed to initiate payment');
        }
      } catch (error) {
        console.error('Error in handleAddFunds:', error);
        ElMessage.error('Failed to initiate payment. Please try again.');
      }
    };

    // The result box is global: don't let it follow the user to the next page
    onBeforeRouteLeave(() => {
      ElMessageBox.close();
    });

    const checkPaymentResult = () => {
      const { paymentSuccess, amount } = route.query;

      if (paymentSuccess !== undefined) {
        // Clean up URL by removing query parameters
        router.replace({ path: '/web/wallet/dashboard', query: {} });

        if (paymentSuccess === 'true') {
          const amountValue = amount ? parseFloat(amount as string).toFixed(2) : '0.00';

          ElMessageBox.alert(
            `$${amountValue} has been successfully added to your wallet!`,
            'Payment Successful',
            {
              confirmButtonText: 'OK',
              type: 'success',
              center: true,
            },
          ).then(() => {
            // Reload wallet details to show updated balance
            loadWalletDetails();
            fetchTransactionsPage(0);
          }).catch(() => {
            // Closed by leaving the page
          });
        } else {
          ElMessageBox.alert(
            'We couldn\'t process your payment. Please try again.',
            'Payment Failed',
            {
              confirmButtonText: 'OK',
              type: 'error',
              center: true,
            },
          ).catch(() => {
            // Closed by leaving the page
          });
        }
      }
    };

    onMounted(() => {
      // Load wallet details and transactions in parallel
      loadWalletDetails();
      fetchTransactionsPage(0);

      // Check for payment result after a short delay to ensure page is loaded
      setTimeout(() => {
        checkPaymentResult();
      }, 500);
    });

    return {
      walletSubtitle,
      isLoading,
      hasWallet,
      walletBalance,
      walletLinks: WALLET_LINKS,
      realAmountInWallet,
      reservedAmountInWallet,
      showDetailedAmounts,
      transactions,
      isLoadingTransactions,
      hasNextPage,
      transactionsError,
      goToCreateWallet,
      handleAddFunds,
      loadMore,
      retryTransactions,
    };
  },
});
</script>

<style lang="scss" scoped>
// Main Layout - matching sticker details page
.wallet-dashboard-page {
  background: #ffffff;
  min-height: 100vh;
  padding-bottom: 64px;

  @media (min-width: 768px) {
    padding-bottom: 80px;
  }

  @media (min-width: 1024px) {
    padding-bottom: 96px;
  }
}

.page-container {
  max-width: 800px;
  padding: 24px 24px 0;

  @media (min-width: 768px) {
    padding: 32px 40px 0;
  }

  @media (min-width: 1024px) {
    padding: 40px 64px 0;
  }
}

// Wallet Card - redesigned with modern style
.wallet-info-card {
  background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
  border-radius: 16px;
  padding: 40px 32px;
  margin-bottom: 24px;
  color: #fff;
  min-height: 240px;
  box-shadow: 0 10px 40px rgba(13, 148, 136, 0.3);
  display: flex;
  flex-direction: column;
  animation: fade-in 0.3s ease-in-out;
  position: relative;
  overflow: hidden;

  @media (min-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    min-height: 220px;
    padding: 40px 48px;
  }

  // Shimmer effect
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 50%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    animation: shimmer-card 3s linear infinite;
  }

  // Gradient overlay for depth
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle at center, rgba(255, 255, 255, 0.15) 0%, transparent 70%);
    pointer-events: none;
  }
}

.wallet-content {
  flex: 1;
  position: relative;
  z-index: 1;
  width: 100%;
}

.wallet-action {
  position: relative;
  z-index: 1;
  margin-top: 8px;

  @media (min-width: 1024px) {
    margin-top: 10px;
  }
}

@keyframes shimmer-card {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.wallet-header {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  z-index: 1;
}

.wallet-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.9;
}

.wallet-balance {
  font-size: 42px;
  font-weight: 900;
  margin-bottom: 8px;
  line-height: 1.2;
}

.wallet-details {
  font-size: 13px;
  opacity: 0.8;
  margin-top: 12px;
  font-weight: 500;
}

.no-wallet {
  text-align: center;
  padding: 32px 0;
  position: relative;
  z-index: 1;

  p {
    margin-bottom: 20px;
    font-size: 16px;
    font-weight: 500;
  }

  .el-button {
    background: #fff;
    color: #0d9488;
    border: none;
    padding: 14px 36px;
    border-radius: 9999px;
    font-weight: 700;
    font-size: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.95);
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: scale(0.98);
    }
  }
}

// Text button for Add Money (inside card)
.text-btn-add-money {
  background: none;
  border: none;
  color: white;
  font-size: 13px;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 1px;
  opacity: 0.9;
  transition: all 0.2s;

  &:hover {
    opacity: 1;
    text-decoration-thickness: 2px;
  }

  &:active {
    opacity: 0.8;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}

// Activity Feed - redesigned section
.wallet-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: -8px 0 8px;
}

.wallet-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 8px 14px 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: #0d9488;
    color: #0d9488;
  }

  &__chevron {
    width: 14px;
    height: 14px;
    opacity: 0.5;
  }
}

.activity-feed {
  margin-top: 32px;
  padding-bottom: 32px;
  animation: fade-in 0.3s ease-in-out 0.1s backwards;
}

.section-header {
  background: transparent;
  padding: 0 0 16px 0;
  margin-bottom: 16px;

  h3 {
    font-size: 24px;
    font-weight: 900;
    color: #1a1a1a;
    margin: 0;
  }
}

.transactions-container {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.transactions-list {
  background: #fff;
}

.transaction-wrapper {
  padding: 0 24px;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }
}

.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0 24px;
}

.empty-state {
  padding: 60px 24px;
  text-align: center;

  p {
    margin: 0;
    color: #6b7280;
    font-size: 16px;
    font-weight: 500;
  }
}

.loading-more {
  padding: 20px;
  text-align: center;

  p {
    margin: 0;
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
  }
}

.btn-load-more {
  width: 100%;
  padding: 16px;
  background: #f3f4f6;
  border: none;
  border-radius: 0 0 16px 16px;
  color: #0d9488;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #0f766e;
  }

  &:active {
    background: #d1d5db;
  }
}

.error-state {
  padding: 32px 24px;
  text-align: center;

  p {
    margin-bottom: 16px;
    color: #ef4444;
    font-size: 14px;
    font-weight: 500;
  }

  .el-button {
    background: #ef4444;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 9999px;
    font-weight: 600;

    &:hover {
      background: #dc2626;
    }
  }
}

// Skeleton loaders - updated colors
.skeleton-wallet {
  background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 40px 32px;
  min-height: 240px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle at center, rgba(255, 255, 255, 0.15) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (min-width: 1024px) {
    min-height: 220px;
    padding: 40px 48px;
  }
}

.skeleton-line {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 25%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 75%
  );
  background-size: 200% 100%;
  animation: shimmer-skeleton 1.5s infinite;
  border-radius: 8px;
}

.skeleton-title {
  width: 140px;
  height: 20px;
  margin-bottom: 12px;
}

.skeleton-balance {
  width: 180px;
  height: 42px;
}

@keyframes shimmer-skeleton {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-transaction {
  padding: 20px 24px;
  display: flex;
  gap: 16px;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '';
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200% 100%;
    animation: shimmer-skeleton 1.5s infinite;
    flex-shrink: 0;
  }

  &::after {
    content: '';
    flex: 1;
    height: 48px;
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200% 100%;
    animation: shimmer-skeleton 1.5s infinite;
    border-radius: 8px;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .wallet-info-card {
    padding: 24px;
    min-height: 180px;
  }

  .wallet-balance {
    font-size: 36px;
  }

  .section-header h3 {
    font-size: 20px;
  }

  .action-btn-single {
    height: 52px;
    font-size: 16px;
  }
}

</style>
